import type { ReactNode } from 'react';
import clsx from 'clsx';

import { useSectionLayoutMode } from '@/hooks';
import { AppScrollArea } from '@/components/ui/AppScrollArea';
import { CollapsibleTabs } from '../CollapsibleTabs';
import type { CollapsibleTabItem } from '../CollapsibleTabs';
import { SectionSidebar } from '../SectionSidebar';
import styles from './DetailPanelLayout.module.css';

export interface DetailPanelLayoutProps {
  /**
   * Section navigation layout. When omitted, resolves automatically from the
   * global SlidePanel fullscreen state via {@link useSectionLayoutMode}
   * (sidebar in desktop fullscreen, tabs otherwise).
   */
  mode?: 'tabs' | 'sidebar';
  sectionItems: CollapsibleTabItem[];
  activeSection: string;
  onSectionChange: (id: string) => void;
  sidebarHeader?: ReactNode;
  children: ReactNode;
  aside?: ReactNode;
  contentVariant?: 'default' | 'wide' | 'flex';
  contentClassName?: string;
  sidebarHeaderBordered?: boolean;
  /**
   * Widen the sidebar content column to a bounded preview width (~1100px)
   * instead of the default 720px reading width. Independent of
   * `contentVariant` (scroll ownership) — `contentVariant === 'wide'` still
   * wins with full width. Use for read-only detail previews.
   */
  widePreview?: boolean;
}

export function DetailPanelLayout({
  mode,
  sectionItems,
  activeSection,
  onSectionChange,
  sidebarHeader,
  children,
  aside,
  contentVariant = 'default',
  contentClassName,
  sidebarHeaderBordered = true,
  widePreview = false,
}: DetailPanelLayoutProps) {
  const { mode: autoMode } = useSectionLayoutMode();
  const resolvedMode = mode ?? autoMode;
  // Width of the sidebar content column. `contentVariant === 'wide'` (full
  // width, delegated scroll) wins; otherwise an opt-in `widePreview` widens to
  // a bounded preview column; otherwise the default 720px reading width.
  const contentWidthClass =
    contentVariant === 'wide'
      ? styles.sidebarContentWide
      : widePreview
        ? styles.sidebarContentPreview
        : undefined;
  // Delegated content owns its own scroll (its inner AppScrollArea / self-scroll
  // regions); the layout must NOT wrap it in another AppScrollArea, or the inner
  // `fullBleedGutter` negative margin overflows the outer scroll box.
  const isDelegated = contentVariant !== 'default';

  if (resolvedMode === 'sidebar') {
    const sidebarContent = (
      <>
        {sidebarHeader && (
          <div
            className={clsx(
              styles.sidebarHeader,
              !sidebarHeaderBordered && styles.sidebarHeaderNoBorder
            )}
          >
            {sidebarHeader}
          </div>
        )}
        {children}
      </>
    );

    return (
      <div className={styles.sidebarLayout}>
        <SectionSidebar
          items={sectionItems}
          activeItem={activeSection}
          onItemChange={onSectionChange}
        />
        {isDelegated ? (
          // Delegated variants own their scroll (see .sidebarContentDelegated).
          <div
            className={clsx(
              styles.sidebarContentWrapper,
              styles.sidebarContentDelegated,
              contentWidthClass,
              contentClassName
            )}
          >
            {sidebarContent}
          </div>
        ) : (
          <AppScrollArea fill>
            <div
              className={clsx(
                styles.sidebarContentWrapper,
                contentWidthClass,
                contentClassName
              )}
            >
              {sidebarContent}
            </div>
          </AppScrollArea>
        )}
        {aside}
      </div>
    );
  }

  // Delegated content manages its own scroll (see .tabPanelDelegated); otherwise
  // the layout wraps it in a single AppScrollArea.
  const tabContent = (
    <div
      className={clsx(
        styles.tabPanel,
        isDelegated && styles.tabPanelDelegated,
        contentClassName
      )}
    >
      {children}
    </div>
  );

  return (
    <div className={styles.tabsContainer}>
      <CollapsibleTabs
        items={sectionItems}
        activeItem={activeSection}
        onItemChange={onSectionChange}
      />
      {isDelegated ? (
        tabContent
      ) : (
        <AppScrollArea fill>{tabContent}</AppScrollArea>
      )}
    </div>
  );
}
